/**
 * G2 — Deal-side presentation adapter.
 * Consumes server-projected G1 fields only; never invents ownership.
 */

import { partyDisplayName } from "./dealFormatting";
import { isTerminalDealStatus } from "./dealLifecycleActions";

export const DEAL_SIDE_KIND = {
  PERSONAL: "personal",
  COMPANY: "company",
};

const SIDE_KIND_LABELS = {
  personal: "Особиста сторона",
  company: "Компанія",
};

const SIDE_TITLES = {
  seller: "Продавець",
  buyer: "Покупець",
};

function normalizeSide(side) {
  return side === "buyer" ? "buyer" : side === "seller" ? "seller" : null;
}

function readSideKind(deal, side) {
  const raw = deal && deal[`${side}_side_kind`];
  if (raw === DEAL_SIDE_KIND.COMPANY) return DEAL_SIDE_KIND.COMPANY;
  // Absent / personal / unknown → personal (G1 backfill + personal fixtures).
  return DEAL_SIDE_KIND.PERSONAL;
}

function isViewerOnSide(deal, side) {
  const role = deal && deal.viewer_role;
  if (role === "both") return true;
  if (role === "seller") return side === "seller";
  if (role === "buyer") return side === "buyer";
  return false;
}

function companyDisplayName(deal, side) {
  const company = deal && deal[`${side}_company`];
  const fromCompany =
    company && (company.name_snapshot || company.name)
      ? company.name_snapshot || company.name
      : null;
  const snapshot = deal && deal[`${side}_company_name_snapshot`];
  const name = fromCompany || snapshot || null;
  return name && String(name).trim() ? String(name).trim() : null;
}

function companyIdOf(deal, side) {
  const company = deal && deal[`${side}_company`];
  if (company && company.id != null && company.id !== "") {
    return company.id;
  }
  return null;
}

/**
 * Safe presentation shape for one Deal side (seller|buyer).
 * @param {object|null|undefined} deal
 * @param {"seller"|"buyer"} side
 */
export function getDealSidePresentation(deal, side) {
  const normalized = normalizeSide(side);
  if (!deal || !normalized) return null;

  const kind = readSideKind(deal, normalized);
  const historicalRealtor = deal[`${normalized}_realtor`] || null;
  const representative =
    kind === DEAL_SIDE_KIND.COMPANY
      ? deal[`${normalized}_representative`] || null
      : null;
  const companyId = kind === DEAL_SIDE_KIND.COMPANY
    ? companyIdOf(deal, normalized)
    : null;
  const companyName =
    kind === DEAL_SIDE_KIND.COMPANY
      ? companyDisplayName(deal, normalized)
      : null;
  const isViewerSide = isViewerOnSide(deal, normalized);
  const displayKindLabel = SIDE_KIND_LABELS[kind] || SIDE_KIND_LABELS.personal;

  let displayName = "—";
  if (kind === DEAL_SIDE_KIND.COMPANY) {
    displayName = companyName || "Компанія";
  } else {
    displayName = partyDisplayName(historicalRealtor);
  }

  return {
    side: normalized,
    sideTitle: SIDE_TITLES[normalized],
    kind,
    companyId,
    companyName,
    historicalRealtor,
    representative,
    isViewerSide,
    displayKindLabel,
    displayName,
  };
}

/**
 * True when the viewer has an independently readable personal Deal side.
 * Uses viewer_role + side_kind only — never historical realtor identity.
 */
export function hasPersonalDealParticipation(deal) {
  if (!deal) return false;
  const role = deal.viewer_role;
  if (role === "seller") {
    return readSideKind(deal, "seller") === DEAL_SIDE_KIND.PERSONAL;
  }
  if (role === "buyer") {
    return readSideKind(deal, "buyer") === DEAL_SIDE_KIND.PERSONAL;
  }
  if (role === "both") {
    return (
      readSideKind(deal, "seller") === DEAL_SIDE_KIND.PERSONAL
      || readSideKind(deal, "buyer") === DEAL_SIDE_KIND.PERSONAL
    );
  }
  return false;
}

/** Company-only Deal access for this viewer (no personal side). */
export function isCompanyOnlyDealAccess(deal) {
  if (!deal || !deal.viewer_role) return false;
  return !hasPersonalDealParticipation(deal);
}

/**
 * Counterparty presentation for list cards (server viewer_role).
 * Prefer company identity over personal realtor when counterparty is company.
 */
export function getCounterpartSidePresentation(deal) {
  if (!deal) return null;
  const role = deal.viewer_role;
  if (role === "seller") return getDealSidePresentation(deal, "buyer");
  if (role === "buyer") return getDealSidePresentation(deal, "seller");
  if (role === "both") {
    // Internal / both: prefer seller side label for compact card summary.
    return getDealSidePresentation(deal, "seller");
  }
  return null;
}

export function formatCounterpartSummary(presentation) {
  if (!presentation) return "—";
  if (presentation.kind === DEAL_SIDE_KIND.COMPANY) {
    const name = presentation.displayName || "Компанія";
    if (
      presentation.representative
      && partyDisplayName(presentation.representative) !== "—"
    ) {
      return `${name} · ${partyDisplayName(presentation.representative)}`;
    }
    return name;
  }
  return presentation.displayName || "—";
}

/**
 * Company work navigation targets using existing E2 routes only.
 * No inventing ?deal= filters.
 */
export function getCompanyWorkLinks(presentation) {
  if (
    !presentation
    || presentation.kind !== DEAL_SIDE_KIND.COMPANY
    || presentation.companyId == null
    || !presentation.isViewerSide
  ) {
    return null;
  }
  const id = presentation.companyId;
  return {
    tasksHref: `/companies/${id}/tasks`,
    viewingsHref: `/companies/${id}/tasks?kind=viewing`,
  };
}

/**
 * UX eligibility for representative reassignment CTA.
 * Backend remains authoritative; this is visibility hardening only.
 */
export function canShowRepresentativeReassignCta({
  presentation,
  company,
  deal,
} = {}) {
  if (!presentation || presentation.kind !== DEAL_SIDE_KIND.COMPANY) {
    return false;
  }
  if (!deal || isTerminalDealStatus(deal.status)) return false;
  if (!presentation.isViewerSide) return false;
  if (presentation.companyId == null) return false;
  if (!company) return false;
  const role = company.my_role
    || (company.my_membership && company.my_membership.role)
    || null;
  const status =
    (company.my_membership && company.my_membership.status) || null;
  if (status && status !== "active") return false;
  return role === "owner" || role === "admin";
}

export function sideKindLabel(kind) {
  return SIDE_KIND_LABELS[kind] || SIDE_KIND_LABELS.personal;
}
